import { UploadUserType } from 'utils/api/types';

import { unwrapExcelSafeCell } from './excelSafeCell';
import { ImportVocabulary, customFieldKey } from './importVocabulary';

type UploadUserTypeKey = keyof UploadUserType;

const CSV_HEADER_MAPPING: Record<string, UploadUserTypeKey> = {
  firstname: 'firstName',
  lastname: 'lastName',
  emailaddress: 'emailAddress',
  phonenumber: 'phoneNumber',
  dateofbirth: 'dateOfBirth',
  memorableword: 'memorableWord',
  loneworkinglicence: 'loneWorkingLicence',
  auditslicence: 'auditsLicence',
  eventslicence: 'eventsLicence',
  documentslicence: 'documentsLicence',
  costcentre: 'costCentre',
  alarmresponseprofile: 'alarmResponseProfile',
  teams: 'teams',
  role: 'role',
  account: 'account',
  jobtitle: 'jobTitle',
  employeeid: 'employeeId',
  startdate: 'startDate',
  country: 'country',
  associatedsite: 'site',
  linemanager: 'manager',
  unit: 'unit',
};

const normalizeHeader = (header: string): string =>
  unwrapExcelSafeCell(header.trim())
    .toLowerCase()
    .replace(/[\s_]+/g, '');

export const transformCsvHeader = (header: string): string => {
  const normalized = normalizeHeader(header);
  return CSV_HEADER_MAPPING[normalized] || normalized;
};

/**
 * Dropdown and custom-field headers are per-org labels, so the mapping is built at modal open. A fixed
 * column wins a collision, as the static mapping already does, and between two dynamic labels the first
 * registered wins, so a custom field renamed to "Department" can never claim the department column.
 * Gated columns are still mapped — the row resolver could otherwise not tell "not supported" from
 * "not in the sheet".
 */
export const createCsvHeaderTransform = (vocabulary: ImportVocabulary): ((header: string) => string) => {
  const dynamic: Record<string, UploadUserTypeKey> = {};

  const add = (label: string, key: UploadUserTypeKey): void => {
    const normalized = normalizeHeader(label);
    if (!normalized || normalized in CSV_HEADER_MAPPING || normalized in dynamic) {
      return;
    }
    dynamic[normalized] = key;
  };

  add(vocabulary.departmentLabel, 'department');
  add(vocabulary.employmentTypeLabel, 'employmentType');
  add(vocabulary.regionLabel, 'region');
  vocabulary.customFields.forEach(({ slot, label }) => {
    add(label, customFieldKey(slot));
    add(`Custom Field ${slot}`, customFieldKey(slot));
  });

  return (header: string): string => {
    const normalized = normalizeHeader(header);
    return CSV_HEADER_MAPPING[normalized] || dynamic[normalized] || normalized;
  };
};

export { CSV_HEADER_MAPPING, normalizeHeader };
